import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { relativeTime } from "@/lib/utils";

interface AccessCode {
  id: string;
  code: string;
  note: string | null;
  created_at: number;
  status: "available" | "used" | "revoked";
  used_at: number | null;
  used_by_email: string | null;
  accepted_by: {
    user_id: string;
    name: string | null;
    email: string;
    orgs: { id: string; name: string; slug: string }[];
  } | null;
}

/** Super-admin-only: mint, view, and remove the single-use codes that gate
 * signup while the product is early-access / invite-only (see
 * backend/routes/auth.ts + Repo's signup_codes methods). */
export function AccessCodesPage() {
  const [codes, setCodes] = useState<AccessCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);

  function load() {
    api<{ ok: true; codes: AccessCode[] }>("/api/admin/signup-codes")
      .then((res) => setCodes(res.codes))
      .catch((err) =>
        setError(
          err instanceof ApiError ? err.message : "failed to load access codes",
        ),
      );
  }

  useEffect(load, []);

  async function handleCreate() {
    setCreating(true);
    try {
      const res = await api<{ ok: true; code: string }>(
        "/api/admin/signup-codes",
        {
          method: "POST",
          body: JSON.stringify({ note: note.trim() || undefined }),
        },
      );
      await navigator.clipboard.writeText(res.code).catch(() => {});
      toast(`Created ${res.code} (copied to clipboard)`);
      setShowCreate(false);
      setNote("");
      load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "failed to create access code",
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm("Revoke this code? It can no longer be redeemed.")) return;
    try {
      await api(`/api/admin/signup-codes/${id}/revoke`, { method: "POST" });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to revoke code");
    }
  }

  async function handleDelete(id: string) {
    if (
      !confirm(
        "Delete this code permanently? This removes it from the list entirely.",
      )
    )
      return;
    try {
      await api(`/api/admin/signup-codes/${id}`, { method: "DELETE" });
      setCodes((prev) => prev?.filter((c) => c.id !== id) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to delete code");
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Access Codes</h1>
          <p className="text-sm text-muted-foreground">
            Pure is early access — signup requires one of these single-use
            codes.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New code</Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All codes{codes ? ` (${codes.length})` : ""}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Accepted by</TableHead>
                <TableHead>Created</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes?.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs">{c.code}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.note ?? "—"}
                  </TableCell>
                  <TableCell>
                    {c.status === "available" && (
                      <Badge variant="success">available</Badge>
                    )}
                    {c.status === "used" && (
                      <Badge variant="secondary">used</Badge>
                    )}
                    {c.status === "revoked" && (
                      <Badge variant="destructive">revoked</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.accepted_by ? (
                      <div className="text-sm">
                        <div>
                          {c.accepted_by.name ?? c.accepted_by.email}{" "}
                          <span className="text-xs text-muted-foreground">
                            ({c.accepted_by.email})
                          </span>
                        </div>
                        {c.accepted_by.orgs.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.accepted_by.orgs.map((o) => (
                              <Badge
                                key={o.id}
                                variant="outline"
                                className="text-[10px]"
                              >
                                {o.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : c.used_by_email ? (
                      // Redeemed, but the account no longer exists (deleted after signup).
                      <span className="text-xs text-muted-foreground">
                        {c.used_by_email} (account deleted)
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(c.created_at)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {c.status === "available" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(c.id)}
                      >
                        Revoke
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(c.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {codes?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No access codes yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create access code</DialogTitle>
            <DialogDescription>
              Optionally note who this is for — helps you track it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 my-4">
            <Label htmlFor="note">Note (optional)</Label>
            <Input
              id="note"
              placeholder="jane@example.com"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
